import * as XLSX from "xlsx";
import { validateCourses } from "./courseValidation";

/**
 * Generates an Excel template for bulk course upload
 * @returns Buffer containing the Excel file
 */
export const generateCourseExcelTemplate = (): Buffer => {
  // Define the column headers
  const headers = [
    "Course Name*", // Required
    "Course Type*", // Required (Core, General, Elective)
    "Description",
    "Objectives",
    "Target Audience",
    "Cost",
    "Degrees", // Comma-separated IDs
    "Number of Modules", // Default: 35
    "Number of Activities", // Default: 2
  ];

  // Create a sample row
  const sampleRow = [
    "Introduction to Computer Science",
    "Core",
    "This course provides an introduction to computer science concepts and programming fundamentals.",
    "Understand basic programming concepts, Learn problem-solving techniques, Develop simple applications",
    "Students interested in technology and programming with no prior experience",
    "99.99",
    "2025-05-01",
    "", // Leave empty for no degrees
    "35", // Default number of modules
    "2", // Default number of activities
  ];

  // Create a worksheet
  const ws = XLSX.utils.aoa_to_sheet([headers, sampleRow]);

  // Set column widths
  const wscols = headers.map(() => ({ wch: 30 })); // Set width to 30 for all columns
  ws["!cols"] = wscols;

  // Create a workbook
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Course Template");

  // Generate buffer
  const buf = XLSX.write(wb, { type: "buffer", bookType: "xlsx" });

  return buf;
};

export interface ParsedCourseResult {
  courses: any[];
  validCourses: any[];
  invalidCourses: any[];
  validationErrors: { field: string; message: string }[];
}

/**
 * Parses an Excel file for bulk course upload
 * @param file The Excel file to parse
 * @returns Object containing parsed courses and validation results
 */
export const parseCourseExcelFile = (file: File): Promise<ParsedCourseResult> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });

        // Get the first sheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        // Map to course objects
        const courses = jsonData.map((row: any) => ({
          courseName: row["Course Name*"],
          courseType: row["Course Type*"],
          description: row["Description"] || "",
          objectives: row["Objectives"] || "",
          targetAudience: row["Target Audience"] || "",
          cost: row["Cost"] ? parseFloat(row["Cost"]) : 0,
          degrees: row["Degrees"]
            ? String(row["Degrees"])
                .split(",")
                .map((id) => id.trim())
            : [],
          numberOfModules: row["Number of Modules"] ? parseInt(row["Number of Modules"]) : 35,
          numberOfActivities: row["Number of Activities"] ? parseInt(row["Number of Activities"]) : 2,
        }));

        // Validate courses
        const validationResult = validateCourses(courses);

        resolve({
          courses,
          validCourses: validationResult.validCourses,
          invalidCourses: validationResult.invalidCourses,
          validationErrors: validationResult.errors,
        });
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = (error) => {
      reject(error);
    };

    reader.readAsArrayBuffer(file);
  });
};
